<template>
    <div class="space-y-4">
        <button
            v-if="selectedEvents.length"
            class="fixed top-14 right-[max(0.75rem,calc(50vw-20rem))] z-20 w-12 h-12 rounded-full bg-primary text-primary-fg shadow-lg flex items-center justify-center text-xl font-bold"
            @click="confirmEvents"
        >
            ✓
        </button>

        <section>
            <h2 class="font-semibold">{{ t('event.chooseEvent') }}</h2>
            <div class="space-y-1">
                <AppCheckbox
                    v-for="ev in upcomingEvents"
                    :key="ev.id"
                    v-model="selectedEvents"
                    :value="ev.id"
                    :label="eventLabel(ev)"
                />
            </div>
        </section>

        <section>
            <h3 class="font-semibold mt-4 flex items-center gap-2">
                {{ t('event.chooseEventPast') }} ({{ olderText }})
                <button v-if="!pastShown" class="text-xs underline" @click="pastShown = true">
                    ⊕ {{ t('event.showPast') }}
                </button>
            </h3>

            <div v-if="pastShown" class="space-y-1">
                <AppCheckbox
                    v-for="ev in pastEvents"
                    :key="ev.id"
                    v-model="selectedEvents"
                    :value="ev.id"
                    :label="`${ev.name} (${formatDate(ev.startAt, 'D.M.YYYY')})`"
                />
            </div>
        </section>

        <AppButton v-if="selectedEvents.length" block class="mt-4" @click="confirmEvents">
            {{ t('event.confirm') }}
        </AppButton>
    </div>
</template>

<script setup lang="ts">
    import { computed, onMounted, ref } from 'vue'
    import { useRouter } from 'vue-router'
    import { useEventsStore, type EventItem } from '@/stores/events'
    import { useSettingsStore } from '@/stores/settings'
    import { useI18n } from 'vue-i18n'
    import AppCheckbox from '@/components/ui/AppCheckbox.vue'
    import AppButton from '@/components/ui/AppButton.vue'
    import { formatDate, isOnOrAfter, isBefore } from '@/services/util/date'

    const events = useEventsStore()
    const settings = useSettingsStore()
    const router = useRouter()
    const { t } = useI18n()

    const selectedEvents = ref<string[]>([...events.activeEvents])
    const pastShown = ref(false)

    const upcomingEvents = computed(() => {
        const threshold = new Date(Date.now() - 86_400_000)
        return events.events
            .filter(
                e =>
                    isOnOrAfter(e.endAt ?? e.startAt ?? new Date().toISOString(), threshold) ||
                    (e.startAt === null && e.endAt === null)
            )
            .sort((a, b) => new Date(a.startAt ?? 0).getTime() - new Date(b.startAt ?? 0).getTime())
    })

    const pastEvents = computed(() => {
        const threshold = new Date(Date.now() - 86_400_000)
        return events.events
            .filter(e => isBefore(e.endAt ?? e.startAt ?? '', threshold))
            .sort((a, b) => new Date(b.startAt ?? 0).getTime() - new Date(a.startAt ?? 0).getTime())
    })

    const olderText = computed(() => {
        switch (settings.olderEvents) {
            case 7:
                return t('settings.olderEventsWeek')
            case 30:
                return t('settings.olderEventsMonth')
            case 90:
                return t('settings.olderEvents3Month')
            case 180:
                return t('settings.olderEvents6Month')
            case 365:
                return t('settings.olderEventsYear')
            default:
                return t('settings.olderEventsAllList')
        }
    })

    function eventLabel(e: EventItem) {
        const sa = e.startAt
        const ea = e.endAt
        const noDates = sa === null && ea === null
        const range = ea
            ? `${sa ? formatDate(sa, 'D.M.YYYY') : ''} - ${formatDate(ea, 'D.M.YYYY')}`
            : sa
              ? formatDate(sa, 'D.M.YYYY')
              : ''
        return `${e.name} (${noDates ? 'neomezená' : range})`
    }

    function confirmEvents() {
        events.setActiveEvents(selectedEvents.value)
        events.setActiveCheckpoint(null)
        events.setLastZones(null)
        events.setActiveZones([])
        void router.push({ name: 'ticketsets' })
    }

    onMounted(async () => {
        try {
            await events.loadEvents(settings.olderEvents)
        } catch {
            // ignore — might be offline
        }
    })
</script>
